import React, { useEffect, useState } from "react";
import { ADOBE_DTM_SCRIPT, ADOBE_DTM_SCRIPT_CN, APPLICATIONS } from "../configs/constants";
import { Global, CacheProvider } from "@emotion/react";
import { getGlobalStyles } from "../themes/global";
import { addTrailingSlash, getPageLocale, isCNTLD } from "../utils/helpers";
import createCache from "@emotion/cache";
import Starfleet from "../services/starfleet";
import { getUserGeoInfo } from "../services/utils";
import Spinner from "./Spinner";
import { GlobalNavLayout } from "./GlobalNavLayout";
const appCache = createCache({
    key: "wrapper-cache",
    container: document.getElementById("wrapper-root"),
});
const isChinaDomain = isCNTLD();

const urlParams = new URLSearchParams(location.search);

const starfleet = new Starfleet({
    urlParams,
    SESSION_ITEM_NAME,
    CLIENT_ID: isChinaDomain ? CLIENT_ID_CN : CLIENT_ID,
    LAST_LOGIN,
    SUPPORTED_IDPS,
    PRIVACY_ITEM_NAME,
    JARVIS_IDP_ID: isChinaDomain ? JARVIS_IDP_ID_CN : JARVIS_IDP_ID,
    LOGIN_HINTS_ITEM_NAME,
    STARFLEET_BASE: isChinaDomain ? STARFLEET_BASE_CN : STARFLEET_BASE,
    VOLTRON_BASE: isChinaDomain ? VOLTRON_BASE_CN : VOLTRON_BASE,
    SSO_TIMEOUT,
});
let session = {};
const locale = getPageLocale();

export const App = () => {
    // const [navbarFooterData, setNavbarFooterData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [theme, setTheme] = useState("light");
    const [activeAppPathList, setActiveAppPathList] = useState([]);
    const [readyToMountNav, setReadyToMountNav] = useState(false);

    useEffect(() => {
        document.documentElement.setAttribute("lang", locale);
    }, []);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            addTrailingSlash(window.location.href, true);

            let applicationName = getApplicationName(window.location.pathname);
            setActiveAppPathList(APPLICATIONS[applicationName].pathList);
            setTheme(APPLICATIONS[applicationName].theme);
            // load session only for:
            // ** applications configured to require login
            // ** loginhint query param is present
            // ** jso query param is present
            if (
                APPLICATIONS[applicationName].loginRequired ||
                urlParams.has("jso") ||
                urlParams.has("loginhint")
            ) {
                try {
                    session = await starfleet.getSession(locale);
                } catch (err) {
                    console.error("Error during the get session call:", err);
                }
                // session does not exist
                // send the user to login
                if (!session) {
                    starfleet.authorize(locale);
                    return null;
                }
                let profileResponse;
                try {
                    profileResponse = await starfleet.getProfile(session.access_token);
                } catch (err) {
                    console.error("Error during the get profile call:", err);
                }

                // if the user has already been presented with the finish profile screen, we load the requested page
                // otherwise the user is redirected to the finish profile page
                if (profileResponse?.data) {
                    const { countryCode } = await getUserGeoInfo();
                    if (
                        starfleet.consentSignupCheck(
                            profileResponse.data.consent,
                            profileResponse.data.id,
                            locale,
                            { tld: isCNTLD() ? "cn" : "com", countryCode }
                        )
                    ) {
                        starfleet.getEmailToken(profileResponse.data?.verifiedEmails[0]?.value);
                        starfleet.getStarfleetToken(session.id_token);
                        starfleet.getanonymousToken();
                    }
                }
            }
            prepareDom(applicationName);
            loadBundle(applicationName);
            setReadyToMountNav(true);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        window.addEventListener("popstate", handleBrowserBack);
        return () => {
            window.removeEventListener("popstate", handleBrowserBack);
        };
    }, []);

    useEffect(() => {
        loadAdobeDTM();
        loadOneTrustSnippet();
    }, []);

    const handleBrowserBack = (e) => {
        try {
            handleNavigate({ url: window.location.href, label: "" }, true);
            e.stopImmediatePropagation();
        } catch (e) {
            console.error("handleBrowserBack: could not stop immediate propagation", e);
        }
    };

    const loadAdobeDTM = () => {
        // Load Adobe DTM script
        let scriptElement = document.createElement("script");
        scriptElement.src = isCNTLD() ? ADOBE_DTM_SCRIPT_CN : ADOBE_DTM_SCRIPT;
        scriptElement.setAttribute("data-ot-ignore", "true");
        document.head.appendChild(scriptElement);
    };

    const loadOneTrustSnippet = () => {
        // Load OneTrust snippet
        if (!document.getElementById("onetrust-snippet")) {
            const script = document.createElement("script");
            script.src = isCNTLD() ? ONE_TRUST_SNIPPET_SCRIPT_CN : ONE_TRUST_SNIPPET_SCRIPT;
            script.setAttribute("data-document-language", true);
            if (isCNTLD()) {
                script.setAttribute("data-language", "zh-CN");
            }
            script.type = "text/javascript";
            script.charset = "UTF-8";
            script.setAttribute(
                "data-domain-script",
                isCNTLD() ? ONE_TRUST_SNIPPET_DOMAIN_CN : ONE_TRUST_SNIPPET_DOMAIN
            );
            script.setAttribute("id", "onetrust-snippet");
            document.head.appendChild(script);
        }
        if (!document.getElementById("onetrust-snippet-2")) {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.setAttribute("id", "onetrust-snippet-2");
            script.text = `
            function OptanonWrapper() {        
                var event = new Event('bannerLoaded');
                window.dispatchEvent(event);
            }
        `;
            document.head.appendChild(script);
        }
        if (!document.getElementById("onetrust-snippet-3")) {
            const script = document.createElement("script");
            script.src = isCNTLD()
                ? ONE_TRUST_SNIPPET_CUSTOM_SCRIPT_CN
                : ONE_TRUST_SNIPPET_CUSTOM_SCRIPT;
            script.type = "text/javascript";
            script.setAttribute("id", "onetrust-snippet-3");
            document.head.appendChild(script);
        }
    };

    const clearOneTrust = () => {
        const oneTrustBanner = document.getElementById("onetrust-consent-sdk");
        if (oneTrustBanner) {
            oneTrustBanner.remove();
        }
    };

    const getApplicationName = (pathname) => {
        for (let applicationName in APPLICATIONS) {
            for (let path of APPLICATIONS[applicationName].pathList) {
                if (pathname.includes(path)) {
                    // should check differently
                    return applicationName;
                }
            }
        }
        return "";
    };

    const prepareDom = (runningApplicationName) => {
        for (const id of APPLICATIONS[runningApplicationName].rootElementIds) {
            const appElement = document.createElement("div");
            appElement.setAttribute("id", id);
            document.getElementById("content").appendChild(appElement);
        }
        delete window.webpackJsonp;
    };

    const loadBundle = (appName) => {
        let scriptElement = document.createElement("script");
        scriptElement.src = APPLICATIONS[appName].bundle;
        scriptElement.setAttribute("defer", "defer");
        document.head.appendChild(scriptElement);
    };

    const clearScripts = () => {
        const scriptsElements = document.head.getElementsByTagName("script");
        for (let i = scriptsElements.length - 1; i >= 0; --i) {
            scriptsElements[i].remove();
        }
    };

    const clearStyles = () => {
        const styleElements = document.head.getElementsByTagName("Style");

        for (let i = styleElements.length - 1; i >= 0; --i) {
            styleElements[i].remove();
        }
    };

    const removePortal = () => {
        const portal = document.getElementById("portal");
        if (portal) {
            portal.remove();
        }
    };

    const handleNavigate = (target, mustReplace) => {
        document.getElementById("content").innerHTML = "";
        removePortal();
        clearStyles();
        clearOneTrust();
        clearScripts();

        (async () => {
            const redirectTo = addTrailingSlash(target.url);
            //why a function because its value can change before setTimeout
            const currentUrl = () => addTrailingSlash(window.location.href, true);
            const newUrl = new URL(redirectTo);
            const applicationName = getApplicationName(newUrl.pathname);

            if (applicationName === "gfn") {
                window.location.href = newUrl.href;
                return;
            }

            setActiveAppPathList(APPLICATIONS[applicationName].pathList);

            if (mustReplace) {
                //mustReplace is true when user clicks on browser back/forward button
                window.history.replaceState({}, null, redirectTo);
                //test that above sustains
                setTimeout(() => {
                    //we already replaced the url, but we double check
                    if (currentUrl() !== redirectTo) {
                        window.history.replaceState({}, null, redirectTo);
                    }
                }, 10);
            } else {
                window.history.pushState({}, null, redirectTo);
            }

            setTheme(APPLICATIONS[applicationName].theme);
            // load session only for applications that need it
            if (APPLICATIONS[applicationName].loginRequired) {
                session = await starfleet.getSession(locale);
                // session does not exist
                // send the user to login
                if (!session) {
                    starfleet.authorize(locale);
                    return null;
                }
            }
            prepareDom(applicationName);
            loadBundle(applicationName);
            loadAdobeDTM();
            if (target.label) {
                document.title = "Account - " + target.label;
            }
        })();
    };

    return (
        <CacheProvider value={appCache}>
            <Global styles={getGlobalStyles(theme)} />
            <GlobalNavLayout
                activeAppPathList={activeAppPathList}
                handleNavigate={handleNavigate}
                readyToMountNav={readyToMountNav}
            >
                {isLoading ? <Spinner></Spinner> : null}
            </GlobalNavLayout>
        </CacheProvider>
    );
};
